import { useState, useCallback, useEffect, useRef } from "react";
import {
  getContactCRMSummary,
  createContact,
  updateContact,
} from "../../../jsx/components/ContactsBoard/services/contacts.service";
import {
  getPipelines,
  getColumns,
} from "../../../jsx/components/ContactsBoard/services/pipeline.service";

/**
 * Hook para lookup, criação e atualização de contato CRM
 * @param {number|null} contactId - ID do contato (vindo da conversa)
 * @param {string} phone - Telefone do contato
 */
const useCRMContact = (contactId, phone) => {
  const [crmContact, setCrmContact] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const staleRef = useRef(false);

  // Buscar contato CRM pelo ID (endpoint otimizado com pipelines+colunas aninhadas)
  const lookupContact = useCallback(async () => {
    if (!contactId) {
      setCrmContact(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    staleRef.current = false;

    try {
      const data = await getContactCRMSummary(contactId);
      if (staleRef.current) return;
      setCrmContact(data);
      // Pipelines com colunas aninhadas vêm no response
      setPipelines(data.pipelines || []);
    } catch (err) {
      if (staleRef.current) return;
      let errorMsg = err.response.data?.error || "Não foi possível obter as informações deste contato, verifique se este contato pertence a você."
      setError(errorMsg);
      setCrmContact(null);
    } finally {
      if (!staleRef.current) setIsLoading(false);
    }
  }, [contactId]);

  // Carregar pipelines (usado apenas pelo CRMCreateForm quando contato não existe)
  const loadPipelines = useCallback(async () => {
    try {
      const result = await getPipelines();
      const list = result?.results || result || [];
      setPipelines(list);
      return list;
    } catch (err) {
      console.error("Erro ao carregar pipelines:", err);
      return [];
    }
  }, []);

  // Carregar colunas de um pipeline (usado apenas pelo CRMCreateForm)
  const loadColumns = useCallback(async (pipelineId) => {
    if (!pipelineId) {
      setColumns([]);
      return [];
    }
    try {
      const result = await getColumns(pipelineId);
      const list = result?.results || result || [];
      setColumns(list);
      return list;
    } catch (err) {
      console.error("Erro ao carregar colunas:", err);
      setColumns([]);
      return [];
    }
  }, []);

  // Criar contato CRM
  const createCRMContact = useCallback(async (data) => {
    setIsCreating(true);
    setError(null);
    try {
      const result = await createContact(data);
      setCrmContact(result);
      return result;
    } catch (err) {
      console.error("Erro ao criar contato CRM:", err);
      setError("Não foi possível criar o contato no CRM");
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, []);

  // Atualizar contato CRM e recarregar dados otimizados
  const updateCRMContact = useCallback(async (data) => {
    if (!crmContact?.id) return null;
    setIsUpdating(true);
    setError(null);
    try {
      await updateContact(crmContact.id, data);
      const refreshed = await getContactCRMSummary(crmContact.id);
      setCrmContact(refreshed);
      setPipelines(refreshed.pipelines || []);
      return refreshed;
    } catch (err) {
      console.error("Erro ao atualizar contato CRM:", err);
      setError("Não foi possível atualizar o contato");
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [crmContact?.id]);

  // Mover para coluna (pode incluir mudança de pipeline)
  const moveToColumn = useCallback(async (columnId, pipelineId) => {
    if (!crmContact?.id) return null;
    setIsUpdating(true);
    try {
      const payload = { column: columnId };
      if (pipelineId) payload.pipeline = pipelineId;
      await updateContact(crmContact.id, payload);
      const refreshed = await getContactCRMSummary(crmContact.id);
      setCrmContact(refreshed);
      setPipelines(refreshed.pipelines || []);
      return refreshed;
    } catch (err) {
      console.error("Erro ao mover contato:", err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [crmContact?.id]);

  // Effect: buscar contato ao montar ou quando contactId mudar
  useEffect(() => {
    staleRef.current = false;
    setCrmContact(null);
    setError(null);

    if (contactId) {
      lookupContact();
    } else {
      loadPipelines();
    }

    return () => {
      staleRef.current = true;
    };
  }, [contactId]);

  return {
    crmContact,
    pipelines,
    columns,
    isLoading,
    isCreating,
    isUpdating,
    error,
    lookupContact,
    createCRMContact,
    updateCRMContact,
    moveToColumn,
    loadColumns,
    loadPipelines,
  };
};

export default useCRMContact;
